import compact from "lodash/compact";
import uniq from "lodash/uniq";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { VoiceSettingsReasonItem } from "@/services/model";

const reasonKey: Partial<Record<VoiceSettingsReasonItem, string>> = {
  [VoiceSettingsReasonItem.BillNotPaid]: "billNotPaid",
  // Dunning starts from an unpaid bill, so it reads the same to the customer.
  [VoiceSettingsReasonItem.BillDunning]: "billNotPaid",
  [VoiceSettingsReasonItem.NoActiveVoicePackage]: "noActiveVoicePackage",
  [VoiceSettingsReasonItem.NoPaymentMean]: "noPaymentMean",
  [VoiceSettingsReasonItem.CreditClassC]: "creditClassC",
  [VoiceSettingsReasonItem.CostLimitReached]: "costLimitReached",
};

/**
 * Why the call settings cannot be changed, ready to drop into the tab's
 * "Good to know" panel. Returns undefined while they are editable.
 */
export const useBlockedVoiceSettingsReason = ({
  blocked,
  isServicePending,
  reasons,
}: {
  blocked: boolean;
  isServicePending: boolean;
  reasons?: VoiceSettingsReasonItem[];
}): ReactNode | undefined => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.voiceSettings.reasons",
  });

  if (!blocked) {
    return undefined;
  }

  if (isServicePending) {
    return t("servicePendingBlocked");
  }

  // Unpaid bills and dunning share a sentence, so the API returning both must
  // not list it twice.
  const translated = uniq(
    compact(
      (reasons ?? []).map((reason) =>
        reasonKey[reason] ? t(reasonKey[reason] as never) : undefined,
      ),
    ),
  );

  // Blocked without a reason the map knows: still say that it is blocked and
  // where to ask, rather than leaving locked switches unexplained.
  if (translated.length === 0) {
    return t("askSupport");
  }

  return (
    <>
      {t("voiceSettingsBlocked")}
      <ul className="list-inside list-disc">
        {translated.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      {t("askSupport")}
    </>
  );
};
